import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

const FundSearch = ({ onAddFund, selectedFundCodes }) => {
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({
    asset_class: [],
  });
  const [searchText, setSearchText] = useState('');
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async (assetClasses = null) => {
    try {
      const params = new URLSearchParams();
      if (assetClasses && assetClasses.length > 0) {
        assetClasses.forEach(ac => params.append('asset_class', ac));
      }
      const response = await api.get(`/api/screener/filters?${params.toString()}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error loading filter options:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value ? [value] : [] };
    setFilters(newFilters);
    if (key === 'asset_class') {
      loadFilterOptions(value ? [value] : null);
    }
  };

  const [allFunds, setAllFunds] = useState([]);

  // Load funds on mount and when asset class filter changes
  const loadFunds = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, values]) => {
        values.forEach(v => params.append(key, v));
      });
      // Include rolled-back privates (NAV pending) — valid holdings for hypothetical portfolios
      params.append('include_rolled', 'true');
      const response = await api.get(`/api/screener/funds?${params.toString()}`);
      const fundsList = response.data.funds || [];
      setAllFunds(fundsList);
      setFunds(fundsList);
    } catch (error) {
      console.error('Error searching funds:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Client-side text filter
  useEffect(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) {
      setFunds(allFunds);
    } else {
      setFunds(allFunds.filter(f =>
        (f.fund_name || '').toLowerCase().includes(term) ||
        (f.tr_code || '').toLowerCase().includes(term) ||
        (f.apir_code || '').toLowerCase().includes(term)
      ));
    }
  }, [searchText, allFunds]);

  // Auto-load when asset class changes (and on initial mount)
  useEffect(() => {
    loadFunds();
  }, [filters]);

  const handleBulkPaste = () => {
    const codes = bulkText
      .split(/[\n,;\s\t]+/)
      .map(c => c.trim().toUpperCase())
      .filter(c => c.length > 0);

    if (codes.length === 0) return;

    const matched = [];
    const notFound = [];

    codes.forEach(code => {
      const fund = allFunds.find(
        f => (f.apir_code || '').toUpperCase() === code ||
             (f.tr_code || '').toUpperCase() === code
      );
      if (fund) {
        if (!selectedFundCodes.includes(fund.tr_code)) {
          matched.push(fund);
        }
      } else {
        notFound.push(code);
      }
    });

    matched.forEach(fund => onAddFund(fund));
    setBulkResults({ added: matched.length, notFound });
  };

  if (loadingFilters) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm text-primary" role="status" />
        <div className="mt-2 text-muted" style={{ fontSize: '0.82rem' }}>Loading filters...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="filters-row">
        <select
          value={filters.asset_class[0] || ''}
          onChange={(e) => handleFilterChange('asset_class', e.target.value)}
        >
          <option value="">All Asset Classes</option>
          {(filterOptions.asset_class || []).map(ac => (
            <option key={ac} value={ac}>{ac}</option>
          ))}
        </select>
      </div>
      <div className="d-flex gap-2 mb-3">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Filter by fund name or code..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ borderColor: 'var(--input-border)' }}
          disabled={loading}
        />
        <button
          className="btn btn-sm btn-outline-secondary"
          style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}
          onClick={() => { setShowBulkPaste(true); setBulkText(''); setBulkResults(null); }}
          disabled={loading}
        >
          Bulk Add
        </button>
      </div>

      {showBulkPaste && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowBulkPaste(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}>
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>Bulk Add Funds</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowBulkPaste(false)} />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '0.85rem', color: 'var(--gray-color)' }}>
                  Paste APIR or TR codes below — one per line, or separated by commas.
                </p>
                <textarea
                  className="form-control"
                  rows={6}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"e.g.\nAAP0103AU\nFSF0003AU\nIOF0145AU"}
                  autoFocus
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                {bulkResults && (
                  <div className="mt-3" style={{ fontSize: '0.85rem' }}>
                    {bulkResults.added > 0 && (
                      <div className="text-success mb-1">
                        {bulkResults.added} fund{bulkResults.added !== 1 ? 's' : ''} added.
                      </div>
                    )}
                    {bulkResults.notFound.length > 0 && (
                      <div className="text-danger">
                        Not found: {bulkResults.notFound.join(', ')}
                      </div>
                    )}
                    {bulkResults.added === 0 && bulkResults.notFound.length === 0 && (
                      <div className="text-muted">All codes already added.</div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkPaste(false)}>
                  Close
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--primary-color)', color: 'var(--white-color)' }}
                  onClick={handleBulkPaste}
                  disabled={!bulkText.trim()}
                >
                  Add Funds
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <div className="mt-2 text-muted" style={{ fontSize: '0.82rem' }}>Searching funds...</div>
        </div>
      )}

      {hasSearched && !loading && (
        <div className="results-count">
          {funds.length} fund{funds.length !== 1 ? 's' : ''} found
        </div>
      )}

      <div className="results-table">
        {funds.length > 0 && (
          <table className="table table-sm">
            <thead>
              <tr>
                <th></th>
                <th>Fund Name</th>
                <th>APIR</th>
                <th>Code</th>
                <th>Asset Class</th>
              </tr>
            </thead>
            <tbody>
              {funds.map(fund => {
                const isSelected = selectedFundCodes.includes(fund.tr_code);
                return (
                  <tr key={fund.tr_code}>
                    <td>
                      <button
                        className="btn-add"
                        onClick={() => onAddFund(fund)}
                        disabled={isSelected}
                        title={isSelected ? 'Already added' : 'Add to portfolio'}
                      >
                        {isSelected ? '✓' : '+'}
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'normal', maxWidth: '250px' }}>{fund.fund_name}</td>
                    <td>{fund.apir_code}</td>
                    <td>{fund.tr_code}</td>
                    <td>{fund.asset_class}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {hasSearched && funds.length === 0 && !loading && (
          <div className="text-center py-3 text-muted">
            No funds found. Try adjusting your filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default FundSearch;
